import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, IconButton, ToggleButtonGroup, ToggleButton, Alert, CircularProgress, Tooltip,
  FormControlLabel, Switch, Link,
} from '@mui/material';
import { Refresh as RefreshIcon, ArrowForward as FlechaIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Embudo de upsell.
//
// El panel de intentos de compra empieza cuando alguien ya tocó "comprar". Este empieza antes:
// cuando un usuario toca una función bloqueada por su plan. Responde qué candados despiertan
// interés, cuántos llegan a ver los planes y cuántos terminan pagando — por función, por
// pantalla de origen y por plan.
// ─────────────────────────────────────────────────────────────────────────────

interface Embudo {
  lockTaps: number;
  ctaTaps: number;
  plansViews: number;
  planSelects: number;
  attempts: number;
  purchases: number;
  usuarios: { lock: number; cta: number; plans: number; select: number; attempt: number; purchase: number };
}

interface PorFeature {
  feature: string;
  requiredTier: string | null;
  lockTaps: number;
  ctaTaps: number;
  usuarios: number;
  usuariosCompraron: number;
  conversion: number | null;
}

interface PorOrigen { source: string; lockTaps: number; ctaTaps: number; plansViews: number }
interface PorPlan { plan: string; planSelects: number; attempts: number; purchases: number }

// Una persona que tocó un candado, con lo necesario para contactarla.
interface Interesado {
  userId: string;
  nombre: string | null;
  email: string | null;
  telefono: string | null;
  clientId: string | null;
  planActual: string;
  toques: number;
  funciones: { feature: string; veces: number }[];
  ultimoToque: string;
  vioPlanes: boolean;
  eligioPlan: boolean;
  intentoPagar: boolean;
  compro: boolean;
  sinVerificar: boolean;
}

interface DatosFunnel {
  dias: number;
  totalEventos: number;
  embudo: Embudo;
  porFeature: PorFeature[];
  porOrigen: PorOrigen[];
  porPlan: PorPlan[];
  interesados?: Interesado[];
  descartados?: number;
}

const cuando = (iso: string) =>
  new Date(iso).toLocaleString('es-DO', { timeZone: 'America/Santo_Domingo', dateStyle: 'short', timeStyle: 'short' });

// Nombres tal como los conoce la app (AppFeatureKey) → como los conoce la gente.
const FEATURE: Record<string, string> = {
  safeZone: 'Zona segura',
  geozones: 'Geozonas',
  maxGeozones: 'Límite de geozonas',
  shareLocation: 'Compartir ubicación',
  batteryAlerts: 'Análisis de batería',
  remoteShutdown: 'Apagado remoto',
  speedLimitAlerts: 'Alertas de velocidad',
  docExpiry: 'Vencimiento de documentos',
  maintenanceScheduling: 'Mantenimiento',
  driverScoringBasic: 'DriveScore',
  weeklyReports: 'Reportes semanales',
  historyDays: 'Historial',
  aggressiveDriving: 'Conducción agresiva',
  antiTheft: 'Antirrobo 360°',
  accidentDetection: 'Detección de accidentes',
  emergencyContact: 'Contactos de emergencia',
  sosAutomatic: 'SOS automático',
  biometricAuth: 'Biometría',
  dashboardWeb: 'Dashboard web',
  prioritySupport: 'Soporte prioritario',
  fuelAnalysis: 'Análisis de combustible',
  scheduleControl: 'Control de horarios',
  driverManagement: 'Gestión de conductores',
  idleDetection: 'Detección de ralentí',
  apiIntegration: 'Integración API',
  desconocida: 'Sin identificar',
};

const ORIGEN: Record<string, string> = {
  map_pill_safezone: 'Mapa · Zona segura',
  map_pill_share: 'Mapa · Compartir ubicación',
  map_pill_battery: 'Mapa · Batería',
  config_prompt: 'Configuración · aviso',
  locked_overlay: 'Configuración · candado',
  config_header: 'Configuración · cabecera',
  settings: 'Ajustes',
  desconocido: 'Sin identificar',
};

const PLAN: Record<string, string> = {
  free: 'Gratis',
  conductor: 'Conductor',
  proteccion: 'Protección',
  empresarial: 'Empresarial',
  sin_plan: '—',
};

const nombreFeature = (k: string) => FEATURE[k] || k;
const nombreOrigen = (k: string) => ORIGEN[k] || k;
const nombrePlan = (k: string | null) => (k ? PLAN[k] || k : '—');

const colorConversion = (c: number | null): 'default' | 'success' | 'warning' | 'error' => {
  if (c == null) return 'default';
  if (c >= 10) return 'success';
  if (c >= 3) return 'warning';
  return 'error';
};

const Etapa: React.FC<{ titulo: string; personas: number; eventos: number; ayuda: string }> = ({ titulo, personas, eventos, ayuda }) => (
  <Tooltip title={ayuda}>
    <Box sx={{ textAlign: 'center', minWidth: 96, px: 1 }}>
      <Typography variant="h5" fontWeight={600}>{personas}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">{titulo}</Typography>
      <Typography variant="caption" color="text.disabled">{eventos} {eventos === 1 ? 'vez' : 'veces'}</Typography>
    </Box>
  </Tooltip>
);

const UpsellFunnelPanel: React.FC = () => {
  const [dias, setDias] = useState(30);
  const [datos, setDatos] = useState<DatosFunnel | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verCompraron, setVerCompraron] = useState(false);
  const navigate = useNavigate();

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await apiService.getUpsellFunnel(dias);
      setDatos(res.data || null);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setCargando(false);
    }
  }, [dias]);

  useEffect(() => { cargar(); }, [cargar]);

  const embudo = datos?.embudo;
  const interesados = (datos?.interesados || []).filter((i) => verCompraron || !i.compro);
  const yaCompraron = (datos?.interesados || []).filter((i) => i.compro).length;
  const sinDatos = !cargando && !error && (!datos || datos.totalEventos === 0);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>Embudo de upsell desde la app</Typography>
            <Typography variant="body2" color="text.secondary">
              Quién toca una función bloqueada por su plan, cuántos llegan a ver los planes y cuántos terminan comprando.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup size="small" exclusive value={dias} onChange={(_, v) => v && setDias(v)}>
              <ToggleButton value={7}>7 días</ToggleButton>
              <ToggleButton value={30}>30 días</ToggleButton>
              <ToggleButton value={90}>90 días</ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Actualizar">
              <IconButton onClick={cargar} color="primary"><RefreshIcon /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>No se pudo cargar el embudo: {error}</Alert>}

        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
        ) : sinDatos ? (
          <Alert severity="info">
            Ningún usuario tocó una función bloqueada en los últimos {dias} días.
            Estos eventos los manda la app a partir de la versión que incluye el embudo; si esperabas ver datos, revisa que esa versión ya esté publicada.
          </Alert>
        ) : embudo && datos && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5, mb: 3, py: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Etapa titulo="Tocaron un candado" personas={embudo.usuarios.lock} eventos={embudo.lockTaps} ayuda="Personas distintas que tocaron una función bloqueada por su plan" />
              <FlechaIcon fontSize="small" color="disabled" />
              <Etapa titulo="Tocaron «Ver planes»" personas={embudo.usuarios.cta} eventos={embudo.ctaTaps} ayuda="Desde el aviso de la función bloqueada" />
              <FlechaIcon fontSize="small" color="disabled" />
              <Etapa titulo="Abrieron Planes" personas={embudo.usuarios.plans} eventos={embudo.plansViews} ayuda="Abrieron la pantalla de planes, desde donde sea" />
              <FlechaIcon fontSize="small" color="disabled" />
              <Etapa titulo="Eligieron un plan" personas={embudo.usuarios.select} eventos={embudo.planSelects} ayuda="Tocaron el botón de un plan antes de que la tienda pidiera el pago" />
              <FlechaIcon fontSize="small" color="disabled" />
              <Etapa titulo="Intentaron pagar" personas={embudo.usuarios.attempt} eventos={embudo.attempts} ayuda="La tienda (Google/Apple) llegó a abrir el pago" />
              <FlechaIcon fontSize="small" color="disabled" />
              <Etapa titulo="Compraron" personas={embudo.usuarios.purchase} eventos={embudo.purchases} ayuda="La app confirmó la compra (purchase_success)" />
            </Box>

            {/* Quién, para poder llamarlo. Primero los que no compraron, con más interés arriba. */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>Interesados que no compraron</Typography>
              {yaCompraron > 0 && (
                <FormControlLabel
                  control={<Switch size="small" checked={verCompraron} onChange={(e) => setVerCompraron(e.target.checked)} />}
                  label={<Typography variant="body2">Incluir los que compraron ({yaCompraron})</Typography>}
                />
              )}
            </Box>
            {interesados.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                {yaCompraron ? 'Todos los que tocaron un candado en este período terminaron comprando.' : 'Nadie tocó un candado en este período.'}
              </Alert>
            ) : (
              <TableContainer sx={{ overflowX: 'auto', mb: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Persona</TableCell>
                      <TableCell>Teléfono</TableCell>
                      <TableCell>Plan actual</TableCell>
                      <TableCell>Qué tocó</TableCell>
                      <TableCell>Hasta dónde llegó</TableCell>
                      <TableCell>Último toque</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {interesados.map((i) => (
                      <TableRow key={i.userId} hover>
                        <TableCell>
                          {i.clientId ? (
                            <Link component="button" underline="hover" fontWeight={600} onClick={() => navigate(`/clients/${i.clientId}`)}>
                              {i.nombre || `Usuario ${i.userId}`}
                            </Link>
                          ) : (
                            <Typography variant="body2" fontWeight={600}>{i.nombre || `Usuario ${i.userId}`}</Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {i.email || 'sin correo'} · #{i.userId}
                          </Typography>
                          {i.sinVerificar && (
                            <Tooltip title="Traccar no respondió al buscarlo: puede ser real, pero no se comprobó">
                              <Chip size="small" color="warning" variant="outlined" label="sin verificar" sx={{ mt: 0.5 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {i.telefono ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Link href={`tel:${i.telefono}`} underline="hover">{i.telefono}</Link>
                              <Tooltip title="Escribir por WhatsApp">
                                <IconButton size="small" color="success" component="a" href={`https://wa.me/${i.telefono.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
                                  <WhatsAppIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">{i.clientId ? 'sin teléfono válido en su ficha' : 'sin ficha de cliente'}</Typography>
                          )}
                        </TableCell>
                        <TableCell><Chip size="small" variant="outlined" label={nombrePlan(i.planActual)} /></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 260 }}>
                            {i.funciones.map((f) => (
                              <Chip key={f.feature} size="small" label={f.veces > 1 ? `${nombreFeature(f.feature)} ×${f.veces}` : nombreFeature(f.feature)} />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {i.compro ? (
                            <Chip size="small" color="success" label="Compró" />
                          ) : i.intentoPagar ? (
                            <Chip size="small" color="warning" label="Intentó pagar" />
                          ) : i.eligioPlan ? (
                            <Chip size="small" color="warning" variant="outlined" label="Eligió un plan" />
                          ) : i.vioPlanes ? (
                            <Chip size="small" variant="outlined" label="Vio los planes" />
                          ) : (
                            <Chip size="small" variant="outlined" label="Solo tocó el candado" />
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">{cuando(i.ultimoToque)}</Typography>
                          <Typography variant="caption" color="text.secondary">{i.toques} {i.toques === 1 ? 'toque' : 'toques'}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {(datos.descartados || 0) > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
                Se omitieron {datos.descartados} {datos.descartados === 1 ? 'usuario que no existe' : 'usuarios que no existen'} ni en el portal ni en Traccar (eventos sin firmar con un id inventado).
              </Typography>
            )}
            {!(datos.descartados || 0) && <Box sx={{ mb: 2 }} />}

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Por función bloqueada</Typography>
            {datos.porFeature.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>Nadie tocó un candado en este período.</Alert>
            ) : (
              <TableContainer sx={{ overflowX: 'auto', mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Función</TableCell>
                      <TableCell>Plan requerido</TableCell>
                      <TableCell align="right">Toques</TableCell>
                      <TableCell align="right">«Ver planes»</TableCell>
                      <TableCell align="right">Personas</TableCell>
                      <TableCell align="right">Compraron después</TableCell>
                      <TableCell align="right">Conversión</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {datos.porFeature.map((f) => (
                      <TableRow key={f.feature} hover>
                        <TableCell>{nombreFeature(f.feature)}</TableCell>
                        <TableCell>{nombrePlan(f.requiredTier)}</TableCell>
                        <TableCell align="right">{f.lockTaps}</TableCell>
                        <TableCell align="right">{f.ctaTaps}</TableCell>
                        <TableCell align="right">{f.usuarios}</TableCell>
                        <TableCell align="right">{f.usuariosCompraron}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Personas que tocaron este candado y compraron cualquier plan después">
                            <Chip size="small" variant="outlined" color={colorConversion(f.conversion)} label={f.conversion == null ? '—' : `${f.conversion}%`} />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 280 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Por pantalla de origen</Typography>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Origen</TableCell>
                        <TableCell align="right">Candados</TableCell>
                        <TableCell align="right">«Ver planes»</TableCell>
                        <TableCell align="right">Abrieron Planes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {datos.porOrigen.map((o) => (
                        <TableRow key={o.source} hover>
                          <TableCell>{nombreOrigen(o.source)}</TableCell>
                          <TableCell align="right">{o.lockTaps}</TableCell>
                          <TableCell align="right">{o.ctaTaps}</TableCell>
                          <TableCell align="right">{o.plansViews}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box sx={{ flex: 1, minWidth: 280 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Por plan</Typography>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Plan</TableCell>
                        <TableCell align="right">Eligieron</TableCell>
                        <TableCell align="right">Intentaron pagar</TableCell>
                        <TableCell align="right">Compraron</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {datos.porPlan.map((p) => (
                        <TableRow key={p.plan} hover>
                          <TableCell>{nombrePlan(p.plan)}</TableCell>
                          <TableCell align="right">{p.planSelects}</TableCell>
                          <TableCell align="right">{p.attempts}</TableCell>
                          <TableCell align="right">{p.purchases}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default UpsellFunnelPanel;
